"use client";

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import Add from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Remove from "@mui/icons-material/Remove";
import { Button, IconButton } from "@mui/material";
import { useTranslations, useMessages } from "next-intl";
import TreatmentCard, { TreatmentCardData } from "./TreatmentCard";
import { trackCtaClick } from "@/utils/commonEvents";
import { appendSessionUtmsToUrl } from "@/utils/utms";

const GET_STARTED_URL = "https://sleep.resmed.com/#insurance-check-section";

const treatmentData: TreatmentCardData[] = [
  {
    id: "positional-therapy",
    titleKey: "positional-therapy",
    descriptionKey: "positional-therapy-is-a-non-invasive",
    imageUrl: "/images/treatments/positional_therapy.webp",
    imageAltKey: "positional-therapy-image-alt",
  },
  {
    id: "oral-appliances",
    titleKey: "oral-appliances",
    descriptionKey: "an-oral-appliance-moves-your-jaw",
    imageUrl: "/images/treatments/oral_appliances.webp",
    imageAltKey: "oral-appliances-image-alt",
  },
  {
    id: "pap",
    titleKey: "pap-therapy",
    descriptionKey: "positive-airway-pressure-therapy",
    imageUrl: "/images/treatments/pap_therapy.webp",
    imageAltKey: "pap-therapy-image-alt",
  },
  {
    id: "glp1s",
    titleKey: "glp1s",
    descriptionKey: "glp1-medications-help-reduce-appetite",
    imageUrl: "/images/treatments/glp1.webp",
    imageAltKey: "glp1s-image-alt",
  },
  {
    id: "nmes",
    titleKey: "nmes",
    descriptionKey: "neuromuscular-electrical-stimulations",
    imageUrl: "/images/treatments/nmes.webp",
    imageAltKey: "nmes-image-alt",
  },
  {
    id: "surgery",
    titleKey: "surgery",
    descriptionKey: "surgical-treatments-for-sleep-apnea",
    imageUrl: "/images/treatments/surgery.webp",
    imageAltKey: "surgery-image-alt",
  },
  {
    id: "coaching",
    titleKey: "coaching",
    descriptionKey: "professional-coaches-can-provide",
    imageUrl: "/images/treatments/coaching.webp",
    imageAltKey: "coaching-image-alt",
  },
];

const AUTO_SCROLL_INTERVAL = 4000;
const GAP_PX = 16; // 1rem gap in pixels (matches gap in _treatment-cards.scss)
const SWIPE_THRESHOLD = 50; // minimum px a touch must travel to count as a swipe

export default function TreatmentCardCarousel() {
  const t = useTranslations("results-page.treatment-cards");
  const tAria = useTranslations("aria");

  const getStartedUrl = appendSessionUtmsToUrl(GET_STARTED_URL);

  // Parse the reference string keys from the translations file
  const strings = useMessages() as Record<
    string,
    Record<string, Record<string, string>>
  >;
  const treatmentCardsStrings = useMemo(
    () => strings["results-page"]?.["treatment-cards"] ?? {},
    [strings],
  );

  const referenceKeys = useMemo(
    () =>
      Object.keys(treatmentCardsStrings)
        .filter((key) => /^ref-\d+$/.test(key))
        .sort((a, b) => {
          const numA = parseInt(a.replace("ref-", ""), 10);
          const numB = parseInt(b.replace("ref-", ""), 10);
          return numA - numB;
        }),
    [treatmentCardsStrings],
  );

  const cardRef = useRef<HTMLDivElement>(null);
  const indicatorRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [cardWidth, setCardWidth] = useState(0);

  const totalCards = treatmentData.length;

  // Measure card width on mount and resize
  useEffect(() => {
    const measure = () => {
      setCardWidth(cardRef.current?.offsetWidth ?? 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Navigate to a card index (handles wrap around using modulo arithmetic)
  const navigateToCard = useCallback(
    (index: number) => {
      const wrappedIndex = ((index % totalCards) + totalCards) % totalCards;
      setActiveIndex(wrappedIndex);
      setIsPlaying(false);
    },
    [totalCards],
  );

  // Each card occupies (cardWidth + gap) px. Multiply by activeIndex
  // to get total pixels the track needs to shift.
  const pixelsToMove = activeIndex * (cardWidth + GAP_PX);

  // Auto-advance every AUTO_SCROLL_INTERVAL ms, wrapping back to the first card.
  // Reduced-motion users still cycle, but the CSS transition is an instant snap.
  useEffect(() => {
    if (!isPlaying) return;

    const timer = setTimeout(() => {
      const nextIndex = activeIndex < totalCards - 1 ? activeIndex + 1 : 0;
      setActiveIndex(nextIndex);
    }, AUTO_SCROLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [isPlaying, totalCards, activeIndex]);

  // Pause auto-scroll on user interaction with the row
  const handleUserInteraction = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Touch swipe: track start and end X positions, then compare the
  // distance against SWIPE_THRESHOLD to decide direction.
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;

    const swipeDistance = touchStart - touchEnd;
    if (swipeDistance > SWIPE_THRESHOLD) {
      // Swiped left → next
      handleUserInteraction();
      setActiveIndex((prev) => (prev < totalCards - 1 ? prev + 1 : 0));
    } else if (swipeDistance < -SWIPE_THRESHOLD) {
      // Swiped right → previous
      handleUserInteraction();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalCards - 1));
    }
  };

  /** Roving tabindex keyboard navigation for indicator dots. */
  const handleIndicatorKeyDown = (
    event: React.KeyboardEvent,
    index: number,
  ) => {
    const keyMap: Record<string, number | undefined> = {
      ArrowLeft: (index - 1 + totalCards) % totalCards,
      ArrowRight: (index + 1) % totalCards,
      Home: 0,
      End: totalCards - 1,
    };

    const newIndex = keyMap[event.key];
    if (newIndex === undefined || newIndex === index) return;

    event.preventDefault();
    navigateToCard(newIndex);
    indicatorRefs.current[newIndex]?.focus();
  };

  return (
    <div
      className="treatment-cards"
      role="region"
      aria-label={tAria("treatment-carousel")}
    >
      {/* Heading Section */}
      <div className="treatment-cards-heading-area">
        <h3 className="treatment-cards-heading">
          {t.rich("heading", {
            managed: (chunks: ReactNode) => (
              <span className="treatment-cards-heading-accent">{chunks}</span>
            ),
          })}
        </h3>
        <p className="treatment-cards-subtitle">{t("subtitle")}</p>
        <Button
          variant="contained"
          className="treatment-cards-get-started-button"
          href={getStartedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackCtaClick({
              componentName: "sleep_assessment_pro",
              clickedArea: "Get started",
              clickedText: t("get-started"),
              clickedUrl: getStartedUrl,
            })
          }
        >
          {t("get-started")}
        </Button>
      </div>

      {/* Carousel Section */}
      <div className="treatment-cards-carousel-area">
        <div
          className="treatment-cards-row"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          aria-live={isPlaying ? "off" : "polite"}
        >
          <div
            className="treatment-cards-track"
            style={{
              transform: `translateX(-${pixelsToMove}px)`,
            }}
          >
            {treatmentData.map((data, i) => (
              <div
                key={data.id}
                className="treatment-card-wrapper"
                ref={i === 0 ? cardRef : undefined}
              >
                <TreatmentCard
                  data={data}
                  onInteraction={handleUserInteraction}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Carousel Controls */}
      <div className="treatment-cards-controls">
        {/* Previous Button */}
        <IconButton
          className="treatment-cards-arrow"
          onClick={() => {
            handleUserInteraction();
            navigateToCard(activeIndex - 1);
          }}
          aria-label={tAria("previous-card")}
        >
          <ArrowBack aria-hidden focusable={false} />
        </IconButton>

        <div
          className="treatment-cards-indicators"
          role="group"
          aria-label={tAria("card-indicators")}
        >
          {/* Indicator Group */}
          {treatmentData.map((data, i) => (
            <button
              key={data.id}
              ref={(button) => {
                indicatorRefs.current[i] = button;
              }}
              type="button"
              className={`treatment-cards-dot${i === activeIndex ? " treatment-cards-dot--active" : ""}`}
              aria-current={i === activeIndex || undefined}
              aria-label={t(data.titleKey)}
              tabIndex={i === activeIndex ? 0 : -1}
              onClick={() => {
                handleUserInteraction();
                navigateToCard(i);
              }}
              onKeyDown={(e) => handleIndicatorKeyDown(e, i)}
            />
          ))}
        </div>

        {/* Next Button */}
        <IconButton
          className="treatment-cards-arrow"
          onClick={() => {
            handleUserInteraction();
            navigateToCard(activeIndex + 1);
          }}
          aria-label={tAria("next-card")}
        >
          <ArrowForward aria-hidden focusable={false} />
        </IconButton>

        {/* Pause Button */}
        <IconButton
          className="treatment-cards-play-pause"
          onClick={() => setIsPlaying((prev) => !prev)}
          aria-label={
            isPlaying ? tAria("pause-carousel") : tAria("play-carousel")
          }
        >
          {isPlaying ? (
            <Pause aria-hidden focusable={false} />
          ) : (
            <PlayArrow aria-hidden focusable={false} />
          )}
        </IconButton>
      </div>

      {/* References Section */}
      <div className="treatment-cards-references">
        <button
          type="button"
          className="treatment-cards-references-toggle"
          onClick={() => setReferencesOpen((prev) => !prev)}
          aria-expanded={referencesOpen}
          aria-label={
            referencesOpen ? t("collapse-references") : t("expand-references")
          }
        >
          <span className="treatment-cards-references-title">
            {t("references-title")}
          </span>
          <IconButton
            component="span"
            className="treatment-cards-references-icon"
            aria-hidden
            tabIndex={-1}
          >
            {referencesOpen ? (
              <Remove aria-hidden focusable={false} />
            ) : (
              <Add aria-hidden focusable={false} />
            )}
          </IconButton>
        </button>
        {referencesOpen && referenceKeys.length > 0 && (
          <ol
            className="treatment-cards-references-list"
            start={parseInt(referenceKeys[0].replace("ref-", ""), 10)}
          >
            {referenceKeys.map((key) => {
              const urlKey = `${key}-url`;
              const hasUrl = urlKey in treatmentCardsStrings;
              return (
                <li key={key}>
                  {t(key)}
                  {hasUrl && (
                    <>
                      {" "}
                      <a
                        href={t(urlKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="treatment-cards-references-link"
                      >
                        {t(urlKey)}
                      </a>
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
